import { MsalGuardConfiguration, MsalInterceptorConfiguration } from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';

import { environment } from 'src/environments/environment';
import { getBaseUrl } from '../../shared/utils/common/common.util';
import { PlatformUtil } from '../../shared/utils/platform/platform.util';

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.debug(message);
}

/**
 * Factory function to create an instance of PublicClientApplication
 */
export function MSALInstanceFactory(): PublicClientApplication {
  const redirectUri = resolveRedirectUri();
  const { authority, knownAuthorities } = resolveAuthorityConfig();

  const instance = new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority,
      redirectUri: redirectUri,
      postLogoutRedirectUri: '/',
      navigateToLoginRequestUrl: PlatformUtil.isNative(),
      knownAuthorities,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: environment.production ? LogLevel.Error : LogLevel.Verbose,
        piiLoggingEnabled: false,
      },
    },
  });
  return instance;
}

/**
 * Factory function to create the MSAL Guard configuration
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  const redirectUri = resolveRedirectUri();
  const scopes = Array.isArray(environment.apiConfig.scopes)
    ? environment.apiConfig.scopes.filter((s) => !!s)
    : [];

  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      ...(scopes.length ? { scopes } : {}),
      redirectUri: redirectUri,
    },
    loginFailedRoute: 'auth/login-failed',
  };
}

/**
 * Factory function to create the MSAL Interceptor configuration
 * @returns MSAL Interceptor configuration
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string> | null>();

  protectedResourceMap.set(environment.onboarding.config, null);
  protectedResourceMap.set(environment.leadsApi.createLead, null);

  protectedResourceMap.set(environment.gatewayDomain, environment.apiConfig.scopes);

  // Also protect local factfind API if configured to a different domain
  const factFindDataDomain = environment.factFind?.load;
  if (factFindDataDomain) {
    try {
      const url = new URL(factFindDataDomain.replace(/\/\{\{.*$/, ''));
      const origin = url.origin;
      if (origin && !protectedResourceMap.has(origin)) {
        protectedResourceMap.set(origin, environment.apiConfig.scopes);
      }
    } catch { /* ignore parse errors */ }
  }

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

/**
 * Factory function to create the MSAL Redirect URI
 * @returns MSAL Redirect URI
 */
function resolveRedirectUri(): string {
  if (PlatformUtil.isNative()) {
    const nativeUri = environment.msalConfig.auth.nativeRedirectUri;
    if (!nativeUri) {
      throw new Error(
        'Native redirect URI (environment.msalConfig.auth.nativeRedirectUri) is not configured.'
      );
    }
    return nativeUri;
  }

  return `${getBaseUrl().origin}${environment.msalConfig.auth.redirectUri}`;
}

/**
 * Factory function to create the MSAL Authority configuration
 * @returns MSAL Authority configuration
 */
function resolveAuthorityConfig(): { authority: string; knownAuthorities: string[] } {
  const authority = environment.b2cPolicies.authorities.signUpSignIn.authority;
  if (!authority) {
    throw new Error(
      'MSAL authority is not configured (environment.b2cPolicies.authorities.signUpSignIn.authority).'
    );
  }

  const knownAuthorities = environment.b2cPolicies.knownAuthorities;
  if (!knownAuthorities || !knownAuthorities.length) {
    throw new Error(
      'MSAL knownAuthorities is not configured (environment.b2cPolicies.knownAuthorities must contain at least one domain).'
    );
  }

  const policyName = environment.b2cPolicies.names.signUpSignIn;
  if (policyName && !authority.toLowerCase().includes(policyName.toLowerCase())) {
    throw new Error(
      `Configured authority does not appear to include the policy name '${policyName}'. Actual authority: ${authority}`
    );
  }

  return { authority, knownAuthorities };
}
